import type { PivotRowDimensionCell, PivotRowResponse } from "../../../../../../../../api/types/svTypes.ts";

export interface PivotRowNode {
  key: string;
  level: number;
  parentKey?: string;
  hasChildren: boolean;
}

export interface PivotRowHierarchy {
  nodes: Map<number, PivotRowNode>;
  groupKeys: Set<string>;
}

export const getPivotRowDimension = (row: PivotRowResponse): PivotRowDimensionCell | undefined => {
  const cell = row["lines"];
  return cell && "dimensionName" in cell ? cell : undefined;
};

export const buildPivotRowHierarchy = (rows: PivotRowResponse[]): PivotRowHierarchy => {
  const nodes = new Map<number, PivotRowNode>();
  const groupKeys = new Set<string>();
  const ancestors: { level: number; node: PivotRowNode; path: string[][] }[] = [];

  for (const row of rows) {
    const cell = getPivotRowDimension(row);
    if (!cell || cell.level < 0) {
      continue;
    }
    while (ancestors.length > 0 && (ancestors.at(-1)?.level ?? -1) >= cell.level) {
      ancestors.pop();
    }
    const parent = ancestors.at(-1);
    const path = [...(parent?.path ?? []), [cell.dimensionName, cell.value]];
    const node: PivotRowNode = {
      key: JSON.stringify(path),
      level: cell.level,
      parentKey: parent?.node.key,
      hasChildren: false,
    };
    if (parent) {
      parent.node.hasChildren = true;
      groupKeys.add(parent.node.key);
    }
    nodes.set(cell.rowId, node);
    ancestors.push({ level: cell.level, node, path });
  }

  return { nodes, groupKeys };
};

export const getVisiblePivotRows = (
  rows: PivotRowResponse[],
  hierarchy: PivotRowHierarchy,
  collapsedKeys: ReadonlySet<string>
): PivotRowResponse[] => {
  const hiddenKeys = new Set<string>();
  return rows.filter((row) => {
    const cell = getPivotRowDimension(row);
    const node = cell && hierarchy.nodes.get(cell.rowId);
    if (!node) {
      return true;
    }
    if (node.parentKey && (hiddenKeys.has(node.parentKey) || collapsedKeys.has(node.parentKey))) {
      hiddenKeys.add(node.key);
      return false;
    }
    return true;
  });
};

export const getPivotLeafRows = (rows: PivotRowResponse[], hierarchy: PivotRowHierarchy): PivotRowResponse[] =>
  rows.filter((row) => {
    const cell = getPivotRowDimension(row);
    return !(cell && hierarchy.nodes.get(cell.rowId)?.hasChildren);
  });

interface PivotGroupExpansionScope {
  keys: string[];
  expanded: boolean;
  hasGroupsAtLevel: boolean;
}

export const getPivotGroupExpansionScope = (
  hierarchy: PivotRowHierarchy,
  collapsedKeys: ReadonlySet<string>,
  level?: number
): PivotGroupExpansionScope => {
  const keys: string[] = [];
  let expanded = false;
  let hasGroupsAtLevel = false;
  for (const node of hierarchy.nodes.values()) {
    if (!node.hasChildren || (level !== undefined && node.level < level)) {
      continue;
    }
    keys.push(node.key);
    if (level === undefined ? !node.parentKey : node.level === level) {
      hasGroupsAtLevel = true;
      expanded ||= !collapsedKeys.has(node.key);
    }
  }
  return { keys, expanded, hasGroupsAtLevel };
};

export const togglePivotGroupLevel = (
  hierarchy: PivotRowHierarchy,
  collapsedKeys: ReadonlySet<string>,
  level?: number
): ReadonlySet<string> => {
  const scope = getPivotGroupExpansionScope(hierarchy, collapsedKeys, level);
  const next = new Set(collapsedKeys);
  scope.keys.forEach((key) => {
    if (scope.expanded) {
      next.add(key);
    } else {
      next.delete(key);
    }
  });
  return next;
};
